import React, { useState, useRef, useMemo, useEffect } from "react";
import { useDesignContext } from "../../contexts/design.context";
import { DesignEditorToolType } from "../../models/enums/design-editor-tool-type.enum";
import { DesignMeasurementLine } from "../../../../../core/models/entities/DesignMeasurementLine";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes, faTrash } from "@fortawesome/free-solid-svg-icons";

export const MeasurementLinesLayer = () => {
  const {
    activeTool,
    setActiveTool,
    selectedView,
    scaledViewInfo,
    measurementLines,
    addMeasurementLine,
    removeMeasurementLine,
    measurementColor,
  } = useDesignContext();

  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [pendingLine, setPendingLine] = useState<DesignMeasurementLine | null>(
    null,
  );
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  if (!scaledViewInfo || !selectedView) return null;

  const scale = scaledViewInfo.styleToViewSizeDelta;

  // Filter lines for current view
  const currentViewLines = useMemo(
    () => measurementLines.filter((l) => l.viewId === selectedView.id),
    [measurementLines, selectedView.id],
  );

  useEffect(() => {
    if (pendingLine && inputRef.current) {
      inputRef.current.focus();
    }
  }, [pendingLine]);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (activeTool !== DesignEditorToolType.Ruler) return;
    if (pendingLine) return; // Don't track mouse when inputting label
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setMousePos({ x, y });
  };

  const handleClick = (e: React.MouseEvent) => {
    if (activeTool !== DesignEditorToolType.Ruler) return;
    if (pendingLine) return; // Ignore clicks on canvas while inputting
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (!startPoint) {
      setStartPoint({ x, y });
    } else {
      // Finish line
      // Apply constraints
      const dx = Math.abs(x - startPoint.x);
      const dy = Math.abs(y - startPoint.y);

      let endX = x;
      let endY = y;

      if (dx > dy) {
        // Horizontal
        endY = startPoint.y;
      } else {
        // Vertical
        endX = startPoint.x;
      }

      // Create pending line
      const newLine: DesignMeasurementLine = {
        viewId: selectedView.id,
        startX: startPoint.x / scale,
        startY: startPoint.y / scale,
        endX: endX / scale,
        endY: endY / scale,
        label: "",
        color: measurementColor,
      };

      setPendingLine(newLine);
      setInputValue("");
      setStartPoint(null);
    }
  };

  const confirmLine = () => {
    if (pendingLine && inputValue.length > 0) {
      addMeasurementLine({ ...pendingLine, label: inputValue + " cm" });
      setPendingLine(null);
      setInputValue("");
      setActiveTool(DesignEditorToolType.Cursor);
    }
  };

  const cancelPendingLine = () => {
    setPendingLine(null);
    setInputValue("");
    // Optionally reset start point if user cancels after second click
    setStartPoint(null);
    setActiveTool(DesignEditorToolType.Cursor);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      confirmLine();
    } else if (e.key === "Escape") {
      cancelPendingLine();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // Allow only numbers, comma, dot
    if (/^[0-9.,]*$/.test(val)) {
      setInputValue(val);
    }
  };

  const getLineFeet = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: string,
  ) => {
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const footLength = 20; // Length of the foot in pixels
    const footAngle = Math.PI / 2; // 90 degrees

    const getFootPoints = (x: number, y: number) => {
      const xStart = x + (footLength / 2) * Math.cos(angle + footAngle);
      const yStart = y + (footLength / 2) * Math.sin(angle + footAngle);
      const xEnd = x + (footLength / 2) * Math.cos(angle - footAngle);
      const yEnd = y + (footLength / 2) * Math.sin(angle - footAngle);
      return { x1: xStart, y1: yStart, x2: xEnd, y2: yEnd };
    };

    const startFoot = getFootPoints(x1, y1);
    const endFoot = getFootPoints(x2, y2);

    return (
      <>
        <line
          x1={startFoot.x1}
          y1={startFoot.y1}
          x2={startFoot.x2}
          y2={startFoot.y2}
          stroke={color}
          strokeWidth="3"
        />
        <line
          x1={endFoot.x1}
          y1={endFoot.y1}
          x2={endFoot.x2}
          y2={endFoot.y2}
          stroke={color}
          strokeWidth="3"
        />
      </>
    );
  };

  const getPreviewLine = () => {
    if (!startPoint || !mousePos) return null;

    const dx = Math.abs(mousePos.x - startPoint.x);
    const dy = Math.abs(mousePos.y - startPoint.y);

    let endX = mousePos.x;
    let endY = mousePos.y;

    if (dx > dy) {
      endY = startPoint.y;
    } else {
      endX = startPoint.x;
    }

    return (
      <>
        <line
          x1={startPoint.x}
          y1={startPoint.y}
          x2={endX}
          y2={endY}
          stroke={measurementColor}
          strokeWidth="3"
          strokeDasharray="9"
        />
        {getLineFeet(startPoint.x, startPoint.y, endX, endY, measurementColor)}
      </>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`absolute top-0 left-0 w-full h-full ${activeTool === DesignEditorToolType.Ruler ? "cursor-crosshair z-20" : "pointer-events-none z-10"}`}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
    >
      <svg className="w-full h-full pointer-events-none">
        {/* Render existing lines */}
        {currentViewLines.map((line, idx) => (
          <g key={idx} className="pointer-events-auto">
            <line
              x1={line.startX * scale}
              y1={line.startY * scale}
              x2={line.endX * scale}
              y2={line.endY * scale}
              stroke={line.color || "red"}
              strokeWidth="3"
              strokeDasharray="9"
            />
            {getLineFeet(
              line.startX * scale,
              line.startY * scale,
              line.endX * scale,
              line.endY * scale,
              line.color || "red",
            )}

            {/* Label */}
            {line.label && (
              <text
                x={((line.startX + line.endX) / 2) * scale}
                y={((line.startY + line.endY) / 2) * scale}
                textAnchor={
                  Math.abs(line.startX - line.endX) >
                  Math.abs(line.startY - line.endY)
                    ? "middle"
                    : "start"
                }
                dominantBaseline="middle"
                fontSize="20"
                fill={line.color || "red"}
                fontWeight="bold"
                dy={
                  Math.abs(line.startX - line.endX) >
                  Math.abs(line.startY - line.endY)
                    ? "-15"
                    : "0"
                }
                dx={
                  Math.abs(line.startX - line.endX) >
                  Math.abs(line.startY - line.endY)
                    ? "0"
                    : "10"
                }
              >
                {line.label}
              </text>
            )}

            {/* Delete button (X) at center */}
            <foreignObject
              x={((line.startX + line.endX) / 2) * scale - 6}
              y={((line.startY + line.endY) / 2) * scale - 6}
              width="12"
              height="12"
            >
              <button
                type="button"
                className="w-3 h-3 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors z-10"
                onClick={(event) => {
                  event.stopPropagation();
                  if (
                    activeTool === DesignEditorToolType.Ruler ||
                    activeTool === DesignEditorToolType.Cursor
                  ) {
                    removeMeasurementLine(line);
                  }
                }}
                aria-label="Remove line"
              >
                <FontAwesomeIcon icon={faTimes} className="text-[8px]" />
              </button>
            </foreignObject>
          </g>
        ))}

        {/* Render preview line */}
        {activeTool === DesignEditorToolType.Ruler &&
          !pendingLine &&
          getPreviewLine()}

        {/* Render pending line being edited */}
        {pendingLine && (
          <>
            <line
              x1={pendingLine.startX * scale}
              y1={pendingLine.startY * scale}
              x2={pendingLine.endX * scale}
              y2={pendingLine.endY * scale}
              stroke={pendingLine.color || "red"}
              strokeWidth="3"
              strokeDasharray="9"
            />
            {getLineFeet(
              pendingLine.startX * scale,
              pendingLine.startY * scale,
              pendingLine.endX * scale,
              pendingLine.endY * scale,
              pendingLine.color || "red",
            )}
          </>
        )}
      </svg>

      {/* Input Field for Pending Line */}
      {pendingLine && (
        <div
          className="absolute bg-white p-1 border border-gray-300 rounded shadow flex items-center gap-1 pointer-events-auto text-md"
          style={{
            left: ((pendingLine.startX + pendingLine.endX) / 2) * scale,
            top: ((pendingLine.startY + pendingLine.endY) / 2) * scale,
            transform: "translate(-50%, -50%)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="w-6 h-4 outline-none text-right"
            placeholder="0"
          />
          <span className="text-gray-600">cm</span>
          <button
            onClick={cancelPendingLine}
            className="text-red-600 hover:text-red-800 ml-1"
          >
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </button>
          <button
            onClick={confirmLine}
            className={`text-green-600 hover:text-green-800 ml-1 ${inputValue.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={inputValue.length === 0}
          >
            <FontAwesomeIcon icon={faCheck} size="lg" />
          </button>
        </div>
      )}

      {/* Instruction Text */}
      {activeTool === DesignEditorToolType.Ruler && !pendingLine && (
        <div className="absolute top-2 left-2 bg-black bg-opacity-75 text-white p-2 rounded text-sm pointer-events-none select-none">
          {!startPoint
            ? "Click to set start point"
            : "Click to set end point (Horizontal/Vertical only)"}
        </div>
      )}
    </div>
  );
};
